import React, { useEffect } from 'react';
import { StoreFinderRedirector } from '@thd-nucleus/app-render';
import { QueryContext, extend } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { EmtCarousel } from '@thd-olt-component-react/card-carousel';
import { GrandOpeningContainer, ProDeskContent, ProDeskFAQ } from '@thd-olt-component-react/emt-generated-content';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { ProDeskMetadata } from '@thd-olt-component-react/metadata';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import { SpecialBuyBanner } from '@thd-olt-component-react/special-buy-banner';
import { NearbyStores } from '../../shared-components/NearbyStores';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';
import { ProDeskInfoPods } from '../../shared-components/ProDeskInfoPods/ProDeskInfoPods';
import { ProDeskHero } from './ProDeskHero';
import { ProDeskProXtraPods } from '../../shared-components/ProDeskProXtraPods/ProDeskProXtraPods';
import { ProDeskProRewards } from '../../shared-components/ProDeskProRewards/ProDeskProRewards';
import { ProDeskResources } from '../../shared-components/ProDeskResources/ProDeskResources';

export const ProDesk = () => {
  declareContexts([QueryContext], { freeze: true });

  const {
    storeNumber,
    storeZip,
    state,
    name,
    city
  } = useParams();
  const path = useLocation();

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.pro-desk-page-load', {
      storeNumber,
      storeZip,
      state,
      name,
      city,
    });
  }, [storeNumber]);

  return (
    <>
      <ErrorBoundary name="sf-pro-desk-redirector">
        <StoreFinderRedirector />
      </ErrorBoundary>
      <div className="sui-max-w-screen-2xl sui-m-auto">
        <div className="sui-flex sui-flex-col sui-px-2 2xl:sui-px-0">
          <ErrorBoundary name="pro-desk-metadata">
            <ProDeskMetadata storeId={storeNumber} />
          </ErrorBoundary>

          <ErrorBoundary name="pro-desk-breadcrumbs">
            <StoreFinderBreadcrumbs includeDirectory pageName="Pro Desk" />
          </ErrorBoundary>
          <ErrorBoundary name="prod-desk-grand-opening-pod">
            <GrandOpeningContainer
              contentId={storeNumber}
              pageName="storeDetails"
            />
          </ErrorBoundary>
          <ErrorBoundary name="pro-desk-store-details">
            <ProDeskHero storeNumber={storeNumber} pageName="proDesk" />
          </ErrorBoundary>

          <ErrorBoundary name="garden-center-emt-carousel">
            <EmtCarousel
              contentId={storeNumber}
              headerTitle="Popular with Pros Near You"
              marginClassName="sui-mt-6"
              pageName="proDesk"
            />
          </ErrorBoundary>

          <ErrorBoundary name="pro-desk-store-info-pods">
            <ProDeskInfoPods />
          </ErrorBoundary>

          <ErrorBoundary name="pro-desk-pro-xtra-pods">
            <ProDeskProXtraPods />
          </ErrorBoundary>

          <ErrorBoundary name="pro-desk-pro-rewards">
            <div className="sui-mt-6">
              <ProDeskProRewards />
            </div>
          </ErrorBoundary>

          <ErrorBoundary name="pro-desk-resources">
            <ProDeskResources />
          </ErrorBoundary>

          <section className="sui-flex sui-flex-col sui-gap-y-8 sui-mt-8">
            <ErrorBoundary name="pro-desk-content">
              <ProDeskContent storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="emt-pro-desk-faq">
              <ProDeskFAQ
                storeId={storeNumber}
                title="Frequently Asked Questions About Pro Desk"
              />
            </ErrorBoundary>

            <ErrorBoundary name="pro-desk-nearby-stores">
              <Hydrator
                id="pro-desk-nearby-stores-map"
                scrollBuffer={800}
              >
                <NearbyStores
                  storeNumber={storeNumber}
                  omitStoreNumber={storeNumber}
                  title="Nearby Stores"
                  pageType="proDesk"
                />
              </Hydrator>
            </ErrorBoundary>

            <ErrorBoundary name="pro-desk-special-buy-banner">
              <Hydrator
                id="pro-desk-special-buy"
                scrollBuffer={500}
              >
                <SpecialBuyBanner specialBuyType="pro" />
              </Hydrator>
            </ErrorBoundary>

            <ErrorBoundary name="pro-desk-popular-categories">
              <PopularCategories
                contentId={storeNumber}
                pageType="proDesk"
              />
            </ErrorBoundary>

            <ErrorBoundary name="pro-desk-explore-more">
              <EmtLinks path={path.pathname} />
            </ErrorBoundary>
          </section>
        </div>
      </div>
    </>
  );
};

ProDesk.displayName = 'ProDesk';
ProDesk.propTypes = {};

ProDesk.dataModel = extend(
  Breadcrumbs,
  EmtCarousel,
  EmtLinks,
  GrandOpeningContainer,
  NearbyStores,
  PopularCategories,
  ProDeskContent,
  ProDeskFAQ,
  ProDeskHero,
  ProDeskMetadata,
  SpecialBuyBanner,
  StoreFinderRedirector
);
